import axios, { InternalAxiosRequestConfig } from "axios"
import { nanoid } from "nanoid"

const connectSource = new Map<string, AbortController>()

const handleBaseURL = () => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_BASE_API_URL
  }

  if (import.meta.env.PROD) {
    return import.meta.env.VITE_BASE_API_URL
      ? `${import.meta.env.VITE_BASE_API_URL}`
      : `${window.location.protocol}//${window.location.host.replace(
          /^[^.]*/,
          import.meta.env.VITE_BASE_API_SUBDOMAIN
        )}`
  }

  return "/api"
}

export const axiosInstance = axios.create({
  baseURL: handleBaseURL(),
  timeout: 60000
})

export const resetBaseURL = () => {
  axiosInstance.defaults.baseURL = handleBaseURL()
}

interface ExtendInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  uniqid: string
  isOriginalBaseURL?: boolean
}

axiosInstance.interceptors.request.use(request => {
  const { url, isOriginalBaseURL } = request as ExtendInternalAxiosRequestConfig

  const id = nanoid()
  const controller = new AbortController()
  connectSource.set(`${id}-${url}`, controller)
  const headers = new axios.AxiosHeaders({
    ...request.headers,
    source: 1
  })
  const baseURL = isOriginalBaseURL ? handleBaseURL() : request.baseURL

  return {
    ...request,
    headers,
    signal: controller.signal,
    baseURL,
    uniqid: id
  }
})

axiosInstance.interceptors.response.use(
  response => {
    const { uniqid, url, isOriginalBaseURL } =
      response.config as ExtendInternalAxiosRequestConfig

    connectSource.delete(`${uniqid}-${url}`)
    if (isOriginalBaseURL) {
      resetBaseURL()
    }

    return response
  },
  error => {
    throw error
  }
)

export const cancelAllRequest = () => {
  connectSource.forEach(controller => {
    controller.abort()
  })
  connectSource.clear()
}

export const cancelSpecifiedRequest = (url: string) => {
  connectSource.forEach((controller, key, source) => {
    if (key.indexOf(url) >= 0) {
      controller.abort()
      source.delete(key)
    }
  })
}

export default axiosInstance
